// src/pages/Vouchers.js
import React, { useState, useEffect, useCallback } from 'react';
import { get, post } from '../utils/api';
import { Spinner, formatDate, Alert } from '../components/Shared';
import { useNavigate } from 'react-router-dom';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

export default function Vouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const data = await get('/api/vouchers/pending');
      setVouchers(data.vouchers || []);
    } finally { setLoading(false); }
  }, []);

  const lastUpdated = useAutoRefresh(load);

  async function markSigned(id, invoiceId) {
    try {
      await post(`/api/vouchers/${id}/signed`, {});
      setMsg({ type: 'success', text: 'Marked as signed' });
      load();
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  }

  async function markReturned(id, invoiceId) {
    try {
      await post(`/api/vouchers/${id}/returned`, {});
      setMsg({ type: 'success', text: 'Voucher marked as returned — invoice complete!' });
      load();
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  }

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pending Vouchers</h1>
          <p className="text-sm text-gray-500 mt-1">{vouchers.length} voucher{vouchers.length !== 1 ? 's' : ''} awaiting action</p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-0.5">
              🔄 Auto-refreshing · Last updated {lastUpdated.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
            </p>
          )}
        </div>
        <button onClick={load} className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
          🔄 Refresh
        </button>
      </div>

      {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} /></div>}

      {vouchers.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-16 text-center">
          <p className="text-5xl mb-3">✅</p>
          <p className="text-green-700 font-semibold text-lg">All vouchers are accounted for!</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Invoice #','Party','Delivery Person','Confirmed','Hours','Signed','Returned','Actions'].map((h,i) => (
                  <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vouchers.map((v, i) => (
                <tr key={v.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/invoices/${v.invoice_id}`)}
                      className="font-semibold text-blue-900 hover:underline">
                      {v.invoice_number}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{v.party_name}</div>
                    <div className="text-xs text-gray-400">{v.city}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{v.delivery_person_name}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(v.confirmed_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`font-semibold text-sm ${v.hours_since_confirmation > 4 ? 'text-red-600' : 'text-amber-600'}`}>
                      {Math.round(v.hours_since_confirmation)}h
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {v.is_signed
                      ? <span className="text-green-600 font-medium text-xs">✅ Signed</span>
                      : <span className="text-red-500 text-xs">❌ Not signed</span>}
                  </td>
                  <td className="px-4 py-3">
                    {v.is_returned_to_office
                      ? <span className="text-green-600 font-medium text-xs">✅ Returned</span>
                      : <span className="text-amber-500 text-xs">⏳ Pending</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {!v.is_signed && (
                        <button onClick={() => markSigned(v.id, v.invoice_id)}
                          className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-500">
                          Sign
                        </button>
                      )}
                      {v.is_signed && !v.is_returned_to_office && (
                        <button onClick={() => markReturned(v.id, v.invoice_id)}
                          className="text-xs bg-blue-900 text-white px-2 py-1 rounded hover:bg-blue-800">
                          Return
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
